import React from 'react';
import { motion } from 'framer-motion';
type RadarEchoProps = {
  /** Overall opacity multiplier (0–1). Default 0.6. Reduce to make it more subtle behind text. */
  intensity?: number;
  /** Number of expanding rings. Default 3. */
  rings?: number;
  /** Duration (s) for one full sweep rotation. Default 8. */
  sweepDuration?: number;
  /** Duration (s) for each ring's expansion cycle. Default 5. */
  ringDuration?: number;
  /** Custom className for the wrapper. */
  className?: string;
};
/**
 * RadarEcho — a continuously-sweeping radar background.
 *
 * Lifted out of FinalCTA so it can be reused as a thematic background for
 * text-heavy sections that want to reinforce the "continuous validation"
 * heritage (Brand Legacy, hacker's-eye view, etc).
 *
 * Renders inside its parent via `absolute inset-0` — make sure the parent is
 * `relative` and that the radar sits BEHIND the content (lower z-index, or
 * place it first in the DOM order with `pointer-events-none`).
 */
export function RadarEcho({
  intensity = 0.6,
  rings = 3,
  sweepDuration = 8,
  ringDuration = 5,
  className = ''
}: RadarEchoProps) {
  return <div aria-hidden="true" className={`absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden ${className}`} style={{
    opacity: intensity
  }} data-id="element-226">
      {/* Rotating sweep — conic gradient that paints a fading arc */}
      <motion.div className="absolute rounded-full" style={{
      width: '80vmax',
      height: '80vmax',
      background: 'conic-gradient(from 0deg, transparent 0%, transparent 80%, rgba(26,143,212,0.05) 98%, rgba(26,143,212,0.4) 100%)'
    }} animate={{
      rotate: 360
    }} transition={{
      duration: sweepDuration,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-227" />

      {/* Expanding ring echoes */}
      {Array.from({
      length: rings
    }).map((_, i) => <motion.div key={i} className="absolute rounded-full border border-[#1a8fd4]/20" style={{
      width: '120px',
      height: '120px'
    }} animate={{
      scale: [1, 6],
      opacity: [0.4, 0]
    }} transition={{
      duration: ringDuration,
      repeat: Infinity,
      delay: i * ringDuration / rings,
      ease: 'linear'
    }} data-id="element-228" />)}

      {/* Pulsing center blip */}
      <div className="absolute w-2 h-2 bg-[#1a8fd4] rounded-full shadow-[0_0_20px_#1a8fd4] animate-pulse" data-id="element-229" />
    </div>;
}